import type {
  ArchiveRecord,
  ClaimInfo,
  DraftEmailIntent,
  DraftEmailResponse,
  InviteSummary,
  ListSubmissionsRequest,
  ListSubmissionsResponse,
  ModerationAction,
  ModerationHistoryEntry,
  ModeratorStatsResponse,
  QueueConfig,
  SavedFilter,
  SavedFiltersResponse,
  SignedUrlResponse,
  StatsResponse,
  UpdateMetadataRequest,
} from '../types/moderation';
import type { IssueDetail, IssueStatus, ListIssuesResponse } from '../types/issues';
import { recordApiCall, reportError } from './issue-reporter';
import { getIdToken } from './auth';

function getApiUrl(): string {
  return (import.meta.env.VITE_API_URL as string) || '';
}

export class ApiError extends Error {
  status: number;
  /** Parsed JSON body when the response had one, otherwise null. */
  body: unknown;
  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
    this.body = body;
  }
}

interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildQueryString(
  query?: Record<string, string | number | boolean | undefined>,
): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function doFetch(
  path: string,
  options: ApiFetchOptions,
  token: string | null,
): Promise<Response> {
  const method = options.method || 'GET';
  let res: Response;
  try {
    res = await rawFetch(path, options, token);
  } catch (err) {
    // Usually the moderator's connection. Kept as a breadcrumb, not reported.
    recordApiCall(method, path, 'network-error');
    throw err;
  }
  recordApiCall(method, path, res.status);
  // 5xx is ours to fix; 4xx is the API telling the moderator something.
  if (res.status >= 500) {
    reportError(new Error(`API error ${res.status} on ${method} ${path}`), {
      endpoint: path,
      status: res.status,
    });
  }
  return res;
}

function rawFetch(
  path: string,
  options: ApiFetchOptions,
  token: string | null,
): Promise<Response> {
  const qs = buildQueryString(options.query);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(`${getApiUrl()}${path}${qs}`, {
    method: options.method || 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
}

/**
 * Like `apiFetch` but does not attach a Cognito ID token — for public
 * endpoints such as `GET /moderation/invite/{token}` and
 * `POST /moderation/accept-invite` which the user calls before they
 * have a signed-in session.
 */
export async function apiFetchPublic<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const res = await doFetch(path, options, null);
  if (!res.ok) {
    let message = `API error ${res.status}`;
    let parsedBody: unknown = null;
    try {
      const text = await res.text();
      if (text) {
        message = `${message}: ${text}`;
        try {
          parsedBody = JSON.parse(text);
        } catch {
          parsedBody = text;
        }
      }
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message, parsedBody);
  }
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  let token = await getIdToken();
  let res = await doFetch(path, options, token);

  if (res.status === 401) {
    // Try refreshing the token once and retry.
    token = await getIdToken();
    res = await doFetch(path, options, token);
  }

  if (!res.ok) {
    let message = `API error ${res.status}`;
    let parsedBody: unknown = null;
    try {
      const text = await res.text();
      if (text) {
        message = `${message}: ${text}`;
        try {
          parsedBody = JSON.parse(text);
        } catch {
          parsedBody = text;
        }
      }
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message, parsedBody);
  }

  // Handle empty body case.
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export function listSubmissions(
  params: ListSubmissionsRequest = {},
): Promise<ListSubmissionsResponse> {
  return apiFetch<ListSubmissionsResponse>('/moderation/submissions', {
    method: 'GET',
    query: {
      status: params.status,
      theme: params.theme,
      decade: params.decade,
      mediaType: params.mediaType,
      sensitive: params.sensitive,
      search: params.search,
      contributorEmail: params.contributorEmail,
      sort: params.sort,
      dir: params.dir,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

export function getSubmission(submissionId: string): Promise<ArchiveRecord> {
  return apiFetch<ArchiveRecord>(
    `/moderation/submissions/${encodeURIComponent(submissionId)}`,
    { method: 'GET' },
  );
}

export function moderateSubmission(
  submissionId: string,
  action: ModerationAction,
  note?: string,
  lastSeenUpdatedAt?: string,
): Promise<{
  ok: true;
  submissionId: string;
  newStatus: string;
  history: ModerationHistoryEntry[];
}> {
  return apiFetch('/moderation/moderate', {
    method: 'POST',
    body: { submissionId, action, note, lastSeenUpdatedAt },
  });
}

export function updateMetadata(
  submissionId: string,
  updates: Partial<UpdateMetadataRequest>,
  lastSeenUpdatedAt?: string,
): Promise<ArchiveRecord> {
  return apiFetch<ArchiveRecord>('/moderation/metadata', {
    method: 'POST',
    body: { submissionId, lastSeenUpdatedAt, ...updates },
  });
}

/**
 * Bulk metadata update — applies the same patch to multiple submissionIds
 * in a single request. Returns the server's `{ updated, failed }` summary.
 */
export function updateMetadataBulk(
  submissionIds: string[],
  updates: Partial<UpdateMetadataRequest>,
): Promise<{ updated: string[]; failed: Array<{ submissionId: string; error: string }> }> {
  return apiFetch('/moderation/metadata', {
    method: 'POST',
    body: { submissionIds, ...updates },
  });
}

export function getStats(): Promise<StatsResponse> {
  return apiFetch<StatsResponse>('/moderation/stats', { method: 'GET' });
}

export function detachFile(
  submissionId: string,
  fileIndex: number,
): Promise<ArchiveRecord> {
  return apiFetch<ArchiveRecord>('/moderation/detach-file', {
    method: 'POST',
    body: { submissionId, fileIndex },
  });
}

export function draftEmail(
  submissionId: string,
  intent: DraftEmailIntent,
): Promise<DraftEmailResponse> {
  return apiFetch<DraftEmailResponse>('/moderation/draft-email', {
    method: 'POST',
    body: { submissionId, intent },
  });
}

export function getSignedUrl(
  submissionId: string,
  fileIndex: number,
): Promise<SignedUrlResponse> {
  return apiFetch<SignedUrlResponse>('/moderation/signed-url', {
    method: 'POST',
    body: { submissionId, fileIndex },
  });
}

export function listSavedFilters(): Promise<SavedFiltersResponse> {
  return apiFetch<SavedFiltersResponse>('/moderation/saved-filters', { method: 'GET' });
}

export function saveSavedFilter(filter: SavedFilter): Promise<SavedFiltersResponse> {
  return apiFetch<SavedFiltersResponse>('/moderation/saved-filters', {
    method: 'POST',
    body: filter,
  });
}

export function deleteSavedFilter(name: string): Promise<SavedFiltersResponse> {
  return apiFetch<SavedFiltersResponse>(
    `/moderation/saved-filters/${encodeURIComponent(name)}`,
    { method: 'DELETE' },
  );
}

export function reRunAi(submissionId: string): Promise<{ ok: true; submissionId: string }> {
  return apiFetch('/moderation/re-run-ai', {
    method: 'POST',
    body: { submissionId },
  });
}

// ---------------------------------------------------------------------------
// Phase 3 — multi-moderator coordination
// ---------------------------------------------------------------------------

export function claimSubmission(
  submissionId: string,
): Promise<{ ok: true; claim: ClaimInfo }> {
  return apiFetch('/moderation/claim', {
    method: 'POST',
    body: { submissionId },
  });
}

export function releaseSubmission(
  submissionId: string,
): Promise<{ ok: true; released: boolean }> {
  return apiFetch('/moderation/release', {
    method: 'POST',
    body: { submissionId },
  });
}

/**
 * Claim the next available submission. When the queue's balance check
 * trips, the server returns 409 with a `NOT_BALANCED` body — callers
 * should catch `ApiError` and inspect `.body` for the underloaded
 * moderator's email. Admins can re-call with `force = true` to bypass.
 */
export function claimNextSubmission(
  force = false,
): Promise<{
  ok: true;
  record: ArchiveRecord;
}> {
  return apiFetch('/moderation/claim-next', {
    method: 'POST',
    query: force ? { force: 'true' } : undefined,
  });
}

export function getQueueConfig(): Promise<QueueConfig> {
  return apiFetch<QueueConfig>('/moderation/queue-config', { method: 'GET' });
}

export function putQueueConfig(config: QueueConfig): Promise<QueueConfig> {
  return apiFetch<QueueConfig>('/moderation/queue-config', {
    method: 'PUT',
    body: config,
  });
}

export function getModeratorStats(): Promise<ModeratorStatsResponse> {
  return apiFetch<ModeratorStatsResponse>('/moderation/moderator-stats', {
    method: 'GET',
  });
}

/**
 * POST the generated backup codes to the save-backup-codes Lambda. The
 * server bcrypt-hashes each code and stores the JSON array on the
 * caller's `custom:backup_codes_hash` attribute — plain codes never
 * leave the Lambda's execution context.
 */
export function saveBackupCodes(codes: string[]): Promise<{ ok: true; count: number }> {
  return apiFetch('/moderation/backup-codes', {
    method: 'POST',
    body: { codes },
  });
}

export function inviteModerator(
  email: string,
  role: 'volunteer' | 'lead_moderator' | 'admin' = 'volunteer',
): Promise<{
  ok: true;
  token: string;
  email: string;
  role: string;
  expiresAt: string;
  inviteUrl: string;
}> {
  return apiFetch('/moderation/invite', {
    method: 'POST',
    body: { email, role },
  });
}

export function suspendModerator(
  email: string,
): Promise<{ ok: true; email: string; suspended: true; releasedClaims: number }> {
  return apiFetch('/moderation/suspend', {
    method: 'POST',
    body: { email },
  });
}

export function unsuspendModerator(
  email: string,
): Promise<{ ok: true; email: string; suspended: false }> {
  return apiFetch('/moderation/unsuspend', {
    method: 'POST',
    body: { email },
  });
}

// Public — no auth needed.
export function fetchInvite(token: string): Promise<InviteSummary> {
  return apiFetchPublic<InviteSummary>(
    `/moderation/invite/${encodeURIComponent(token)}`,
    { method: 'GET' },
  );
}

export function acceptInvite(
  token: string,
  email: string,
): Promise<{ ok: true; role: string }> {
  return apiFetchPublic('/moderation/accept-invite', {
    method: 'POST',
    body: { token, email },
  });
}

// ---------------------------------------------------------------------------
// Issues (lead moderators and admins only)
// ---------------------------------------------------------------------------

export function listIssues(): Promise<ListIssuesResponse> {
  return apiFetch<ListIssuesResponse>('/moderation/issues', { method: 'GET' });
}

export function getIssue(id: string): Promise<IssueDetail> {
  return apiFetch<IssueDetail>(`/moderation/issues/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export function updateIssue(
  id: string,
  update: { status: IssueStatus; note?: string },
): Promise<IssueDetail> {
  return apiFetch<IssueDetail>(`/moderation/issues/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: update,
  });
}
